import { push } from '@lagunovsky/redux-react-router'
import PropTypes from 'prop-types'
import React from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { getSandreLabel } from 'utils/StringUtil'
import { PATH_REFERENCIAL_CONTACT_LINK } from '../../home/constants/RouteConstants'
import DtoCampaignInstallationWithStats from '../../installation/components/campaign/dto/DtoCampaignInstallationWithStats'
import ContactDto from '../../referencial/components/contact/dto/ContactDto'
import { getColorCampaign } from '../../utils/CampaignUtil'
import { getDate } from '../../utils/DateUtil'
import CampaignDto from '../dto/CampaignDto'
import DtoCampaignProgress from '../dto/DtoCampaignProgress'
import { getCampaignIcon, getCampaignProgress, getPixelSize, getProgressBar, getQualityCampaignIcon } from '../utils/CampaignUtils'
import Spinner from 'components/progress/Spinner'
import { Box, Card, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { hasValue } from 'utils/NumberUtil'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import CreateIcon from '@mui/icons-material/Create'
import StarRoundedIcon from '@mui/icons-material/StarRounded'
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded'
import { STATION_TYPE_NAME } from 'station/constants/StationConstants'
import { favOrange } from '../../utils/constants/ColorTheme'
import { isNil } from 'lodash'
import ColorConstant from '../../components/constants/ColorConstant'
import { CAMPAIGN_STATUT_VALUES } from '../constants/CampaignConstants'

const getBorderColor = (isArchived, progressValue, statut) => {
    if (statut === CAMPAIGN_STATUT_VALUES.FINALIZE) {
        return ColorConstant.GREEN
    }
    if (statut === CAMPAIGN_STATUT_VALUES.ARCHIVED) {
        return ColorConstant.GREY
    }
    const color = getColorCampaign(isArchived, progressValue, statut)
    return color === ColorConstant.WHITE ? ColorConstant.LIGHTGREY : color
}

const CampaignCard = ({
    progress = {},
    campaign = {},
    contact = {},
    stationType = '',
    onClickLink,
    isBookmarked,
}) => {
    const {
        sandreCodes,
    } = useSelector(store => ({
        sandreCodes: store.ReferencialReducer.sandreCodes,
    }), shallowEqual)
    const dispatch = useDispatch()

    const {
        id,
        idCampaign,
        name,
        statut,
        isArchived,
        campaignType,
        beginningApplication,
        endingApplication,
        campaignStatus,
    } = campaign
    const {
        id: idProgress,
        nbStation = 0,
        nbStationValidated = 0,
        startDate,
        endDate,
        progress: progressValidated,
        progressTotal,
    } = progress

    const progressValue = getCampaignProgress(progressValidated, progressTotal)
    const isQuality = stationType === STATION_TYPE_NAME.quality
    const isProgressLoading = statut !== 1 && [STATION_TYPE_NAME.quality, STATION_TYPE_NAME.piezometry].includes(stationType) && !hasValue(idProgress)
    const statusLabel = getSandreLabel(sandreCodes, 'CAMPAGNES.STATUT', statut || campaignStatus) ?? i18n.unknowStatus
    const period = `${startDate ? getDate(startDate) : beginningApplication ?? ''} - ${endDate ? getDate(endDate) : endingApplication ?? ''}`

    const getProgressContent = () => {
        if (statut === 1) {
            return <CreateIcon color='action' />
        }
        if (isProgressLoading) {
            return <Spinner />
        }
        if (statut !== 2) {
            return null
        }
        return (
            <Stack spacing={0.5} alignItems='flex-start'>
                <Typography variant='body2' color='text.secondary'>
                    {`${stationType === STATION_TYPE_NAME.installation ? i18n.nbInstallationsValidated : i18n.nbStationsValidated} : ${nbStationValidated} / ${nbStation}`}
                </Typography>
                {isQuality ? getProgressBar(progressValidated, progressTotal, progressValue, i18n.analyseOn, i18n.analysisOn) : getProgressBar(progressValidated, progressTotal, progressValue)}
            </Stack>
        )
    }

    return (
        <Card
            variant='outlined'
            onClick={() => dispatch(push(onClickLink || `/${stationType}/campaign/${id || idCampaign}`))}
            sx={{
                marginBottom: 1,
                borderLeft: `6px solid ${getBorderColor(isArchived, progressValue, statut || campaignStatus).toLowerCase()}`,
                backgroundColor: statut === 1 ? 'grey.100' : 'white',
                cursor: 'pointer',
                transition: 'box-shadow 0.2s',
                '&:hover': { boxShadow: 3 },
            }}
        >
            <Stack direction='row' alignItems='center' spacing={2} sx={{ padding: '10px 16px' }}>
                <Box sx={{ display: 'flex', fontSize: getPixelSize('small') }}>
                    {isQuality ? getQualityCampaignIcon(campaignType, 'small') : getCampaignIcon(campaignType, 'small')}
                </Box>
                <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.5}>
                    <Stack direction='row' alignItems='center' spacing={0.5}>
                        {!isNil(isBookmarked) && (
                            <Tooltip title={i18n.bookmark}>
                                {isBookmarked ? <StarRoundedIcon sx={{ color: favOrange }} /> : <StarBorderRoundedIcon sx={{ color: favOrange }} />}
                            </Tooltip>
                        )}
                        <Typography fontWeight='bold' noWrap>{name}</Typography>
                    </Stack>
                    <Typography variant='body2' color='text.secondary'>
                        {`${i18n.period} : ${period}`}
                    </Typography>
                </Stack>
                <Chip label={statusLabel} size='small' variant='outlined' />
                <Box sx={{ width: '200px', display: 'flex', justifyContent: 'center' }}>
                    {getProgressContent()}
                </Box>
                <Tooltip title={contact.name || i18n.unknown}>
                    <IconButton
                        onClick={e => {
                            e.stopPropagation()
                            return contact.code && dispatch(push(`${PATH_REFERENCIAL_CONTACT_LINK}${contact.code}`))
                        }}
                    >
                        <AccountCircleIcon fontSize='large' />
                    </IconButton>
                </Tooltip>
            </Stack>
        </Card>
    )
}

CampaignCard.propTypes = {
    campaign: PropTypes.oneOfType([
        PropTypes.instanceOf(CampaignDto),
        PropTypes.instanceOf(DtoCampaignInstallationWithStats),
    ]),
    progress: PropTypes.instanceOf(DtoCampaignProgress),
    contact: PropTypes.instanceOf(ContactDto),
    stationType: PropTypes.string,
    onClickLink: PropTypes.func,
    isBookmarked: PropTypes.bool,
}

export default CampaignCard
